import { useEffect, useId, useRef, useState } from 'react';
import { Save } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { DatePicker } from '@/components/ui/DatePicker';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import type { Option } from '@/constants/institute';
import type { HolidayFormValues } from '@/features/institute/holidays/schemas/holiday.schema';
import { AUDIENCE_OPTIONS, CATEGORY, CATEGORY_OPTIONS, CLASS_SCOPE_OPTIONS } from '../constants';
import {
  EMPTY_EVENT_FORM,
  eventSchema,
  type EventFormValues,
  type EventPayload,
} from '../schemas/event.schema';
import type { CalendarEventCategory, CalendarEventItem } from '../types/calendar';

export interface EventFormModalProps {
  /** Always an event — a holiday is edited in the holiday form instead. */
  eventItem: CalendarEventItem | null;
  isOpen: boolean;
  onClose: () => void;
  /** Resolves once the server has it. Rejects with a message to show in place. */
  onSave: (payload: EventPayload) => Promise<void>;
  /** The day a new entry opens on — the cell the reader clicked. */
  defaultDate?: string;
  /** The kind a new entry opens as — chosen from the add menu. */
  defaultCategory?: CalendarEventCategory;
  /**
   * Hands the entry over to the holiday form when "Holiday" is picked as its
   * category — a new one, or a saved event being turned into a holiday. Passed
   * only to someone who may declare holidays; without it the option is not
   * offered.
   */
  onChooseHoliday?: (draft: HolidayDraft) => void;
}

/** What has been typed so far, carried into the holiday form so it is not lost. */
export type HolidayDraft = Pick<HolidayFormValues, 'name' | 'startDate' | 'endDate'>;

/**
 * Not an event category: a holiday is a row in `holidays`, the table the
 * attendance register reads, so choosing it switches forms rather than values.
 */
const HOLIDAY_CHOICE = 'HOLIDAY';
const HOLIDAY_OPTION: Option = { value: HOLIDAY_CHOICE, label: CATEGORY.HOLIDAY.label };

/** Which field each validation message belongs against. */
type FieldErrors = Partial<Record<keyof EventFormValues, string>>;

/**
 * Putting something on the institute's calendar.
 *
 * Built on the shared `Modal` rather than on its own overlay, and that is not
 * only tidiness (CLAUDE.md §3). The hand-rolled version drew itself with
 * `position: fixed` *inside the page*, and every console page's root carries
 * `animate-hero-zoom`, which sets `will-change: transform` — that makes the
 * page a containing block, so the "full screen" backdrop was laid out against
 * the page's own box instead of the viewport and the dialog landed in the
 * middle of the content rather than over it.
 *
 * `Modal` is a native `<dialog>`, so the browser puts it in the top layer where
 * no ancestor can contain it, and focus trapping, Escape-to-close and the
 * scroll lock come from the platform rather than from three hand-written
 * effects that will eventually get one of them wrong.
 *
 * The rules come from `eventSchema`, which mirrors the server's, rather than
 * from checks written out here: two copies of "the end cannot precede the
 * start" is how the form and the API end up disagreeing about what is valid.
 */
export function EventFormModal({
  eventItem,
  isOpen,
  onClose,
  onSave,
  defaultDate,
  defaultCategory,
  onChooseHoliday,
}: EventFormModalProps) {
  const formId = useId();
  const formRef = useRef<HTMLFormElement>(null);

  const [values, setValues] = useState<EventFormValues>(EMPTY_EVENT_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  /** What the server said, when it refused what the form was happy with. */
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  function set<K extends keyof EventFormValues>(key: K, value: EventFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  useEffect(() => {
    if (eventItem) {
      setValues({
        title: eventItem.title,
        // The grid draws holidays too, but they never reach this form — their
        // category is not one this endpoint writes.
        category: eventItem.category as EventFormValues['category'],
        startDate: eventItem.startDate,
        endDate: eventItem.endDate,
        startTime: eventItem.startTime ?? '',
        endTime: eventItem.endTime ?? '',
        audience: eventItem.audience,
        classScope: eventItem.classScope,
        description: eventItem.description ?? '',
      });
    } else {
      setValues({
        ...EMPTY_EVENT_FORM,
        startDate: defaultDate ?? '',
        category: defaultCategory ?? '',
      });
    }
    setFieldErrors({});
    setFormError(null);
  }, [eventItem, isOpen, defaultDate, defaultCategory]);

  // Offered on a saved event too. Turning one into a holiday moves it between
  // tables, which is the page's to do once the holiday form has saved.
  const categoryOptions: readonly Option[] = onChooseHoliday
    ? [...CATEGORY_OPTIONS, HOLIDAY_OPTION]
    : CATEGORY_OPTIONS;

  function handleCategoryChange(value: string) {
    if (value === HOLIDAY_CHOICE && onChooseHoliday) {
      onChooseHoliday({ name: values.title, startDate: values.startDate, endDate: values.endDate });
      return;
    }
    set('category', value as EventFormValues['category']);
  }

  /**
   * Brings the reason a save failed into view.
   *
   * The form scrolls inside the modal, and the save button sits below it: by
   * the time somebody presses it they are usually at the description, with the
   * server's refusal drawn at the top and a field error possibly above the
   * fold. Without this a refused save looks like a button that does nothing.
   */
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    if (formError) {
      form.querySelector('[role="alert"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    form.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [formError, fieldErrors]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    // An entry with no end is a one-day entry, not an open-ended one.
    const parsed = eventSchema.safeParse({ ...values, endDate: values.endDate || values.startDate });

    if (!parsed.success) {
      const errors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof EventFormValues | undefined;
        // First message per field wins — a field with two failing rules should
        // say one thing, not stack them.
        if (field && !errors[field]) {
          // If endDate is empty and startDate is empty, do not show error on optional endDate.
          if (field === 'endDate' && !values.endDate && !values.startDate) {
            continue;
          }
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      setFormError(null);
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSaving(true);

    try {
      await onSave(parsed.data);
      onClose();
    } catch (caught) {
      // Held here rather than raised as a toast: the entry is still on screen
      // and still unsaved, so the message belongs beside it.
      setFormError(
        caught instanceof Error ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={eventItem ? 'Edit academic event' : 'Add academic event'}
      description="It appears on the institute calendar and on the schedules of whoever it is addressed to."
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          {/* Submits the form below by id: the footer is outside it, so a bare
              submit button there would belong to no form at all. */}
          <Button
            type="submit"
            form={formId}
            isLoading={isSaving}
            leftIcon={<Save className="h-4 w-4" />}
          >
            {eventItem ? 'Update event' : 'Save to calendar'}
          </Button>
        </>
      }
    >
      <form ref={formRef} id={formId} onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not save this event">
            {formError}
          </Alert>
        )}

        <Input
          label="Event title"
          required
          value={values.title}
          onChange={(e) => set('title', e.target.value)}
          error={fieldErrors.title}
          placeholder="Independence Day, Term II examinations"
          autoComplete="off"
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Category"
            required
            placeholder="Select category"
            options={categoryOptions}
            value={values.category}
            error={fieldErrors.category}
            onChange={(e) => handleCategoryChange(e.target.value)}
          />
          <Select
            label="Which classes"
            required
            placeholder="Select classes"
            options={CLASS_SCOPE_OPTIONS}
            value={values.classScope}
            error={fieldErrors.classScope}
            onChange={(e) => set('classScope', e.target.value as EventFormValues['classScope'])}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DatePicker
            label="Starts"
            required
            value={values.startDate}
            error={fieldErrors.startDate}
            onChange={(e) => set('startDate', e.target.value)}
          />
          <DatePicker
            label="Ends (optional)"
            placeholder="Same day"
            isClearable
            min={values.startDate || undefined}
            value={values.endDate}
            error={fieldErrors.endDate}
            onChange={(e) => set('endDate', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="From (optional)"
            type="time"
            value={values.startTime}
            hint="Leave both empty for an all-day entry"
            error={fieldErrors.startTime}
            onChange={(e) => set('startTime', e.target.value)}
          />
          <Input
            label="Until (optional)"
            type="time"
            value={values.endTime}
            error={fieldErrors.endTime}
            onChange={(e) => set('endTime', e.target.value)}
          />
        </div>

        <Select
          label="Who it is for"
          required
          placeholder="Select audience"
          options={AUDIENCE_OPTIONS}
          value={values.audience}
          error={fieldErrors.audience}
          onChange={(e) => set('audience', e.target.value as EventFormValues['audience'])}
        />

        <Textarea
          label="Description (optional)"
          value={values.description}
          error={fieldErrors.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Brief details or instructions for this event"
          rows={3}
        />
      </form>
    </Modal>
  );
}
